// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { DayLedgerResponse, NutritionTotals, Profile } from "@bitebalance/contracts"
import { useI18n, formatKcal } from "@/i18n"

export interface DailyTargetBarProps {
  totals: NutritionTotals
  calorieTarget: Profile["calorieTarget"] | null
  /** @deprecated 2026-09-10 — the verdict is the server's `dayState`; kept so callers need not change. */
  toleranceCaloriesPercent: number
  /** Authoritative day ledger (ADR-035). Null while loading/unavailable. */
  ledger?: DayLedgerResponse | null
}

/**
 * Today's kcal summary. 2026-09-02 acceptance fix: the bar is CONSUMED
 * progress — outcome-recorded meals + off-plan entries from the server day
 * ledger — never the planned total dressed up as progress. Planned/projected
 * info stays visible but clearly separate ("Planned for today: …"), and the
 * verdict judges the PROJECTION (eaten + still-planned) against the target,
 * exactly like mobile and Amy (owner 2026-08-30 eaten-centric decision).
 * Without a ledger (fetch failed) it degrades to the planned totals — the
 * only honest data available — with no "eaten" claim.
 */
export function DailyTargetBar({ totals, calorieTarget, ledger = null }: DailyTargetBarProps) {
  const { t, locale } = useI18n()

  if (!calorieTarget) {
    return (
      <div className="rounded-bb-xl border border-line bg-paper p-4 text-sm text-ink-2 shadow-sm">
        {t("views.today.targetBarNoTarget")}
      </div>
    )
  }

  const target = calorieTarget.kcal

  // Bar fill = what was actually eaten; the verdict is the SERVER's day state,
  // relayed. Founder decision 2026-09-10: the accepted tolerance moved to the
  // authoritative owner (`services/planning` ledger `dayState`, built from the
  // profile's own toleranceKcalPct), so this component no longer re-derives a
  // second verdict from the same tolerance. It used to — which is how the same
  // day could read calmly here and "Above your target range" in coral on
  // mobile. With no served state there is no verdict yet, which is honest.
  const fillKcal = ledger ? ledger.consumedKcal : totals.kcal
  const judgeKcal = ledger ? ledger.projectedKcal : totals.kcal

  const dayState = ledger?.dayState?.state ?? null
  const percent = Math.max(0, Math.min(100, Math.round((fillKcal / target) * 100)))

  const statusLabel =
    dayState === "above_target"
      ? t("views.today.targetBarOverRange")
      : dayState === "below_floor" || dayState === "below_target_above_floor"
        ? t("views.today.targetBarUnderRange")
        : dayState === "on_target"
          ? t("views.today.targetBarWithinRange")
          : null

  const barColor =
    dayState === "above_target" ? "bg-coral" : dayState === "below_floor" ? "bg-gold" : "bg-green"
  const mealsAhead = ledger ? ledger.plannedRemainingKcal > 0 : false
  // "Planned for today" is the PLAN's whole-day total (server
  // `plannedTodayKcal`, 2026-09-04) — the projection (eaten + still planned)
  // lives in the ledger strip below with its own explanation. Older payloads
  // without the field fall back to the projection, as before.
  const plannedTodayKcal = ledger ? (ledger.plannedTodayKcal ?? judgeKcal) : judgeKcal

  return (
    /* T21 (ADR-070): the day is the page's headline, not one bordered card among
       five of equal weight. Same two server figures, promoted — the eaten count
       leads in the serif with the target beside it, and the rail beneath carries
       the fill. No border, no card: the page itself is the surface. */
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="flex items-baseline gap-2 text-ink-2">
          <span className="font-display text-4xl leading-none font-semibold text-ink md:text-5xl">
            {formatKcal(fillKcal, locale)}
          </span>
          <span className="text-sm">/ {formatKcal(target, locale)}</span>
          {ledger && (
            <span className="text-xs font-semibold text-green-2 uppercase">
              {t("views.today.targetBarEatenTag")}
            </span>
          )}
        </p>
        <p className="text-sm font-semibold text-ink-2">{t("views.today.targetBarLabel")}</p>
      </div>
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("views.today.targetBarLabel")}
        className="h-2.5 w-full overflow-hidden rounded-bb-full bg-cream"
      >
        <div
          className={`h-full ${barColor} rounded-bb-full transition-all`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        {statusLabel && <p className="text-xs text-ink-2">{statusLabel}</p>}
        {mealsAhead && (
          <p className="text-xs text-ink-2">
            {t("views.today.targetBarPlannedToday", { kcal: formatKcal(plannedTodayKcal, locale) })}
          </p>
        )}
      </div>
    </div>
  )
}
